/**
 * The ONE canonical cursor/aim primitive (#565, expanded IN PLACE from
 * #352's plain mutable singleton — decision 1). Three responsibilities live
 * here, and nowhere else:
 *
 *   1. COLLECT — the shared last-known CANVAS pointer position. `InputSurface`
 *      is the sole writer (`setLastCanvasPoint`, on every pointer move over
 *      the surface); `ActionBar`'s HUD range indicator, click-to-cast aim,
 *      and keyboard-cast aim (`MapperContext.lastPointer`) all read it via
 *      `getLastCanvasPoint`. `null` before the pointer has moved over the
 *      canvas (or in this test process) — unchanged from #352.
 *   2. CONVERT — canvas → world via the canonical `screenToWorld`
 *      (`render/projection.ts`, unchanged) plus the shared clamp/brand helper
 *      `canvasPointToWorldUnits` (`mapper.ts`) — the SAME conversion every
 *      other aim consumer uses, not a second ad-hoc pairing.
 *   3. SEND — `startAimPointStream` is the ONLY code that constructs the
 *      `aimPoint` `SimInput` (#565) and is the ONE wire-send path for it:
 *      a fixed-cadence interval, started once by `InputSurface` on mount,
 *      that samples the current canvas position EVERY TICK — unconditional,
 *      independent of pointer-move events, button state, movement mode, or
 *      any toggle (decision 2), but CADENCE-BOUNDED to ≤ 1 send per tick
 *      interval (#796, see `startAimPointStream`). HUD-side render sampling
 *      (`ActionBar`, at its own 10 Hz cadence) stays independently
 *      throttled — that is a display concern, not a protocol one.
 *
 * WHY A SINGLETON, NOT THE STORE: routing raw pointer moves through the
 * store's `useReducer` would re-render the ENTIRE tree on every move —
 * `InputSurface` can see far more of those than the HUD's 10 Hz sample rate
 * needs. See #352's original rationale (preserved from before #565).
 */

import type { SimInput } from "@pit/protocol";
import { canvasPointToWorldUnits, type CanvasPoint } from "./mapper.js";
import { TickThrottle } from "./throttle.js";
import type { ProjectionCenter } from "../render/projection.js";

export interface TrackedCanvasPoint {
  readonly x: number;
  readonly y: number;
}

let latest: TrackedCanvasPoint | null = null;

/** Record the latest known canvas-relative pointer position (or `null`). */
export function setLastCanvasPoint(point: TrackedCanvasPoint | null): void {
  latest = point;
}

/** The latest known canvas-relative pointer position, or `null` if the
 *  pointer hasn't moved over the surface yet (or in this test process). */
export function getLastCanvasPoint(): TrackedCanvasPoint | null {
  return latest;
}

/** #565: per-tick cadence for the `aimPoint` wire stream — matches every
 *  other client-side tick-rate approximation (`InputSurface`'s own
 *  `TICK_RATE_HZ`, `MatchScreen`'s `TICK_RATE`); this module is self-
 *  contained on purpose (no shared constant to import without a new cross-
 *  cutting module, out of scope here). */
const AIM_POINT_STREAM_INTERVAL_MS = 1000 / 30;

export interface AimPointStreamOptions {
  /** Send a `SimInput` over the wire (typically `WsClient.sendInput`). */
  readonly send: (input: SimInput) => void;
  /** Live camera-center getter — read every tick (the follow camera moves
   *  every frame; a captured value would go stale immediately). */
  readonly getProjectionCenter: () => ProjectionCenter;
  /** Override the send cadence — tests inject a smaller/larger interval. */
  readonly intervalMs?: number;
  /** Monotonic clock the #796 cadence throttle measures against. Defaults to
   *  `performance.now()`; tests inject a fake clock so a COALESCED backlog
   *  (several interval callbacks running at the same real instant) can be
   *  expressed at all — under fake timers `performance.now()` barely moves,
   *  which is exactly what a real backlog flush looks like. */
  readonly nowMs?: () => number;
}

export interface AimPointStreamHandle {
  /** Stop the interval. Idempotent. */
  readonly stop: () => void;
}

/**
 * Start the unconditional per-tick `aimPoint` wire stream (#565, decision 2).
 * `InputSurface` calls this ONCE on mount and stops it on unmount. Every tick,
 * if a canvas position is known (`getLastCanvasPoint() !== null`) and it
 * converts to a legal world point, sends `{ kind: "aimPoint", x, y }` —
 * unconditionally, regardless of whether the cursor actually moved since the
 * last tick, so the sim's persisted `Entity.aimPoint` (and the facing derived
 * from it) never goes stale just because the player's mouse is holding
 * still. No message is sent before the first pointer move (cursor unknown).
 *
 * #796: the SEND is cadence-bounded to ≤ 1 per `intervalMs` by the same
 * `TickThrottle` the cast keypath uses, measured on `nowMs` (real time), NOT
 * on the number of interval callbacks. `setInterval` is a wall-clock timer
 * with no phase relationship to the server's tick boundary, and browsers
 * coalesce/defer timers under main-thread jank and then run the backlog in a
 * burst — several callbacks at effectively the same instant. Every one of
 * those sends is an `input` message that the server charges against its
 * per-tick, per-seat cap (`maxInputsPerTick`, pipeline stage [7]), so a
 * bunched aim stream steals budget from the player's REAL inputs and gets
 * them dropped as "rateLimited". Throttling on real elapsed time collapses a
 * burst back to one send.
 *
 * This is a CADENCE bound, not a movement gate: the stream is still
 * unconditional (#565 decision 2) — a stationary cursor keeps sending one
 * `aimPoint` per interval, so the sim's persisted `aimPoint`/facing never goes
 * stale. Dropping a bunched extra costs nothing: `aimPoint` is last-write-wins
 * and the next interval re-sends the current cursor position anyway.
 */
export function startAimPointStream(opts: AimPointStreamOptions): AimPointStreamHandle {
  const intervalMs = opts.intervalMs ?? AIM_POINT_STREAM_INTERVAL_MS;
  const now = opts.nowMs ?? ((): number => performance.now());
  const throttle = new TickThrottle(1000 / intervalMs);
  const id = setInterval(() => {
    const point: CanvasPoint | null = latest;
    if (point === null) return;
    const w = canvasPointToWorldUnits(point, opts.getProjectionCenter());
    if (w === null) return;
    // Spend the slot only when there is something to send — a null cursor or
    // an out-of-bounds conversion must not burn the interval's send budget.
    if (!throttle.tryConsume(now())) return;
    opts.send({ kind: "aimPoint", x: w.x, y: w.y });
  }, intervalMs);
  return {
    stop: (): void => {
      clearInterval(id);
    },
  };
}
